"use client";

import { c as _c } from "react/compiler-runtime";
import { Button } from "@/components/rac/button";
import { Sheet, SheetContent, SheetOverlay, SheetTrigger } from "@/components/rac/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/radix/avatar";
import { RelationshipDialog } from "@/components/relationship-dialog";
import { RelationshipToggle } from "@/components/relationship-toggle";
import { useAuth } from "@/hooks/use-auth";
import { useCurrentCityName } from "@/hooks/use-current-city";
import { useLoginDialog } from "@/hooks/use-login-dialog";
import { trackNavItem } from "@/utils/analytics/track-navigation";
import { cn } from "@/utils/cn";
import { logger } from "@/utils/logger";
import { BookOpen, Flag, HelpCircle, Home, LayoutDashboard, LogOut, Menu, Phone, Plus, Search, TableOfContents, User, Users, X } from "lucide-react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import React from "react";
import { getAvatarUrl, getInitials } from "../signin/utils";
interface MobileProfileMenuProps {
  className?: string;
}
export const MobileProfileMenu: React.FC<MobileProfileMenuProps> = t0 => {
  const $ = _c(32);
  const {
    className
  } = t0;
  const {
    isAuthenticated,
    user,
    logout,
    isLoading
  } = useAuth();
  const {
    openDialog
  } = useLoginDialog();
  const currentCityName = useCurrentCityName();
  const pathname = usePathname();
  const router = useRouter();
  const [showDialog, setShowDialog] = React.useState(false);
  const [pendingRelationship, setPendingRelationship] = React.useState(null);
  const [dialogType, setDialogType] = React.useState("guard");
  let t1;
  if ($[0] !== logout || $[1] !== router) {
    t1 = async close => {
      ;
      try {
        await logout();
        close();
        router.push("/");
      } catch (t2) {
        const error = t2;
        logger.error("Logout failed:", error);
      }
    };
    $[0] = logout;
    $[1] = router;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleLogout = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = (relationship, type) => {
      setPendingRelationship(relationship);
      setDialogType(type);
      setShowDialog(true);
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleShowDialog = t2;
  let t3;
  if ($[4] !== className) {
    t3 = cn("h-10 w-10 rounded-full hover:bg-gray-100 transition-colors duration-200 flex items-center justify-center bg-transparent border-0 cursor-pointer", className);
    $[4] = className;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== isAuthenticated || $[7] !== user) {
    t4 = isAuthenticated && user ? <Avatar size="sm" className="h-8 w-8"><AvatarImage src={getAvatarUrl(user)} alt={user.name} /><AvatarFallback className="bg-primary text-white text-sm font-medium">{getInitials(user.name)}</AvatarFallback></Avatar> : <Menu className="h-6 w-6" aria-hidden="true" />;
    $[6] = isAuthenticated;
    $[7] = user;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className="sr-only">Toggle menu</span>;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t3 || $[11] !== t4) {
    t6 = <SheetTrigger className={t3} aria-label="Open menu">{t4}{t5}</SheetTrigger>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== currentCityName || $[14] !== handleLogout || $[15] !== isAuthenticated || $[16] !== isLoading || $[17] !== openDialog || $[18] !== pathname || $[19] !== user) {
    t7 = <SheetOverlay className="z-[9999]"><SheetContent side="right" className="w-full max-w-[390px] p-0 bg-white/95 backdrop-blur-sm overflow-y-auto border-l-0 z-[10000]" closeButton={false}>{t8 => {
          const {
            close: close_0
          } = t8;
          return <div className="flex flex-col min-h-screen"><div className="bg-gradient-to-b from-[oklch(var(--primary-600))] to-[oklch(var(--primary-400))] px-6 py-6 relative min-h-[280px]"><div className="flex justify-between items-center mb-6">{isAuthenticated ? <RelationshipToggle variant="compact" onAfterSwitch={close_0} onShowDialog={handleShowDialog} /> : <div className="flex items-center bg-black/12 rounded-full p-[2px] relative"><div className={cn("absolute top-[2px] w-[50px] h-[29px] bg-white rounded-full transition-transform duration-200", pathname === "/sell" ? "translate-x-[52px]" : "translate-x-[2px]")} /><Link href="/buy" onClick={close_0} className={cn("relative z-10 flex items-center justify-center h-[29px] px-3 rounded-full text-sm font-medium transition-colors", pathname === "/buy" || pathname === "/" ? "text-neutral-900" : "text-white")}>Buy</Link><Link href="/sell" onClick={close_0} className={cn("relative z-10 flex items-center justify-center h-[29px] px-3 rounded-full text-sm font-medium transition-colors", pathname === "/sell" ? "text-neutral-900" : "text-white")}>Sell</Link></div>}<div className="flex items-center gap-2 relative right-[-10px]">{isAuthenticated ? <Link href="/sell" onClick={close_0} className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-full text-white text-sm font-medium transition-colors duration-200"><Plus className="h-4 w-4" /><span>Post Property</span></Link> : <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-full text-white text-sm font-medium transition-colors duration-200 h-auto cursor-pointer" onClick={() => openDialog()}><Plus className="h-4 w-4" /><span>Post Property</span></div>}<Button variant="ghost" size="icon" className="h-8 w-8 text-white hover:text-gray-20" aria-label="Close menu" onPress={close_0}><X className="h-5 w-5" /></Button></div></div>{isAuthenticated && user ? <div className="flex flex-col items-center text-center mb-6"><div className="w-[82px] h-[82px] bg-white rounded-full mb-4 flex items-center justify-center overflow-hidden"><Avatar size="xl" className="w-full h-full border-0"><AvatarImage src={getAvatarUrl(user)} alt={user.name} /><AvatarFallback className="bg-white text-primary text-xl font-semibold w-full h-full">{getInitials(user.name)}</AvatarFallback></Avatar></div><div className="w-[170px]"><h2 className="text-lg font-medium text-neutral-900 mb-1 font-sans">Hello, {user.name || "User"}</h2><p className="text-sm text-neutral-800/70 font-sans">{user.phones?.[0]?.number || "+91 XXXXXXXXXX"}</p></div></div> : !isLoading ? <div className="flex flex-col items-center text-center mb-6"><div className="w-[82px] h-[82px] bg-white rounded-full mb-4 flex items-center justify-center"><User className="w-8 h-8 text-primary" /></div><div className="w-[170px]"><h2 className="text-lg font-medium text-neutral-900 mb-1 font-dm-sans">Welcome</h2><p className="text-sm text-neutral-800/70 font-dm-sans">Log in to continue</p></div></div> : <div className="flex flex-col items-center text-center mb-6"><div className="w-[82px] h-[82px] bg-white/20 rounded-full mb-4 animate-pulse" /><div className="w-[170px] space-y-2"><div className="w-32 h-6 bg-white/20 rounded mb-1 animate-pulse mx-auto" /><div className="w-24 h-4 bg-white/20 rounded animate-pulse mx-auto" /></div></div>}</div><div className="flex-1 px-6 py-4 bg-white">{!isLoading ? <>{!isAuthenticated && <div className="mb-6"><div className="text-center"><div className="w-full h-12 rounded-lg font-medium text-base inline-flex items-center justify-center whitespace-nowrap bg-primary text-primary-foreground shadow-lg cursor-pointer hover:bg-primary/90 transition-colors" onClick={() => openDialog()}>Log in</div></div></div>}<div className="mb-6"><MenuSection><MenuItem href={isAuthenticated ? "/profile" : undefined} icon={Home} onClick={isAuthenticated ? close_0 : () => openDialog()} trackingAction="profile-home">Profile</MenuItem>{isAuthenticated && <MenuItem href="/dashboard" icon={LayoutDashboard} onClick={close_0} trackingAction="dashboard">Dashboard</MenuItem>}<MenuItem href={isAuthenticated ? "/preference" : `/${currentCityName}`} icon={Search} onClick={close_0} trackingAction="preferences">Resume Search</MenuItem></MenuSection></div><div className="mb-6"><MenuSection><MenuItem href={isAuthenticated ? "/sell" : undefined} icon={Plus} onClick={isAuthenticated ? close_0 : () => openDialog()} badge="Free" trackingAction="add-property" trackingCategory="cta">Add a property to sell</MenuItem></MenuSection></div><div className="mb-6"><SectionTitle>Our Story</SectionTitle><MenuSection><MenuItem href="/about" icon={Users} onClick={close_0} trackingAction="about">About Us</MenuItem><MenuItem href="/pricing" icon={TableOfContents} onClick={close_0} trackingAction="pricing">Pricing and Services</MenuItem><MenuItem href="/faq" icon={HelpCircle} onClick={close_0} trackingAction="faq">FAQ</MenuItem><MenuItem href="/blog" icon={BookOpen} onClick={close_0} trackingAction="blog">Blog</MenuItem></MenuSection></div><div className="mb-6"><SectionTitle>Contact</SectionTitle><MenuSection><MenuItem href="/about" icon={Flag} onClick={close_0} trackingAction="report-bug">Report a bug</MenuItem><MenuItem href="/contact" icon={Phone} onClick={close_0} trackingAction="contact">Contact Us</MenuItem>{isAuthenticated && <MenuItem onClick={() => handleLogout(close_0)} icon={LogOut} className="text-error-600 hover:text-error-700" isButton={true} trackingAction="logout">Log Out</MenuItem>}</MenuSection></div></> : <div className="space-y-4">{Array.from({
                  length: 6
                }).map(_temp)}</div>}</div><div className="px-6 py-3 text-xs text-neutral-600 border-t border-neutral-200 bg-white"><div className="text-center">Settlin © {new Date().getFullYear()} • v1.2.6</div></div></div>;
        }}</SheetContent></SheetOverlay>;
    $[13] = currentCityName;
    $[14] = handleLogout;
    $[15] = isAuthenticated;
    $[16] = isLoading;
    $[17] = openDialog;
    $[18] = pathname;
    $[19] = user;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t6 || $[22] !== t7) {
    t8 = <Sheet>{t6}{t7}</Sheet>;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== dialogType || $[25] !== pendingRelationship || $[26] !== showDialog || $[27] !== user) {
    t9 = showDialog && pendingRelationship && user && <RelationshipDialog isOpen={showDialog} onClose={() => {
      setShowDialog(false);
      setPendingRelationship(null);
    }} currentRelationship={user.relation as "buyer" | "seller"} requiredRelationship={dialogType === "guard" ? pendingRelationship : undefined} targetRelationship={dialogType === "selection" ? pendingRelationship : undefined} featureName={`${pendingRelationship === "seller" ? "Seller" : "Buyer"} Features`} description={`Switch to ${pendingRelationship} mode to access ${pendingRelationship}-specific features`} />;
    $[24] = dialogType;
    $[25] = pendingRelationship;
    $[26] = showDialog;
    $[27] = user;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== t8 || $[30] !== t9) {
    t10 = <>{t8}{t9}</>;
    $[29] = t8;
    $[30] = t9;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  return t10;
};

// Helper Components
const SectionTitle: React.FC<{
  children: React.ReactNode;
}> = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <h3 className="text-sm font-bold text-neutral-900 mb-2 font-dm-sans leading-[21px]">{children}</h3>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const MenuSection: React.FC<{
  children: React.ReactNode;
}> = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="space-y-0 rounded-xl overflow-hidden bg-white border border-neutral-100 divide-y divide-neutral-100">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
interface MenuItemProps {
  children: React.ReactNode;
  href?: string;
  icon: React.ComponentType<{
    className?: string;
  }> | (() => React.ReactNode);
  onClick?: () => void;
  className?: string;
  badge?: string;
  isButton?: boolean;
  trackingAction?: string;
  trackingCategory?: string;
}
const MenuItem: React.FC<MenuItemProps> = t0 => {
  const $ = _c(29);
  const {
    children,
    href,
    icon: Icon,
    onClick,
    className,
    badge,
    isButton: t1,
    trackingAction,
    trackingCategory: t2
  } = t0;
  const isButton = t1 === undefined ? false : t1;
  const trackingCategory = t2 === undefined ? "mobile-navigation" : t2;
  let t3;
  if ($[0] !== Icon) {
    t3 = <div className="flex items-center gap-2 w-5 h-5"><Icon className="w-5 h-5" /></div>;
    $[0] = Icon;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== children) {
    t4 = <span className="flex-1 text-sm font-medium">{children}</span>;
    $[2] = children;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== badge) {
    t5 = badge && <span className="px-2 py-1 text-xs font-medium text-white bg-success-600 rounded">{badge}</span>;
    $[4] = badge;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t3 || $[7] !== t4 || $[8] !== t5) {
    t6 = <>{t3}{t4}{t5}</>;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const content = t6;
  let t7;
  if ($[10] !== onClick || $[11] !== trackingAction || $[12] !== trackingCategory) {
    t7 = () => {
      if (trackingAction) {
        trackNavItem({
          action: trackingAction,
          category: trackingCategory
        });
      }
      onClick?.();
    };
    $[10] = onClick;
    $[11] = trackingAction;
    $[12] = trackingCategory;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const handleClick = t7;
  let t8;
  if ($[14] !== className) {
    t8 = cn("flex items-center gap-2 px-2 py-3 transition-colors duration-200", "hover:bg-neutral-50 active:bg-neutral-100", "text-neutral-900 font-dm-sans font-medium text-sm leading-[21px]", className);
    $[14] = className;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const baseClasses = t8;
  if (isButton) {
    let t9;
    if ($[16] !== baseClasses || $[17] !== content || $[18] !== handleClick) {
      t9 = <button onClick={handleClick} className={baseClasses}>{content}</button>;
      $[16] = baseClasses;
      $[17] = content;
      $[18] = handleClick;
      $[19] = t9;
    } else {
      t9 = $[19];
    }
    return t9;
  }
  if (href) {
    let t9;
    if ($[20] !== baseClasses || $[21] !== content || $[22] !== handleClick || $[23] !== href) {
      t9 = <Link href={href} onClick={handleClick} className={baseClasses}>{content}</Link>;
      $[20] = baseClasses;
      $[21] = content;
      $[22] = handleClick;
      $[23] = href;
      $[24] = t9;
    } else {
      t9 = $[24];
    }
    return t9;
  }
  let t9;
  if ($[25] !== baseClasses || $[26] !== content || $[27] !== handleClick) {
    t9 = <button onClick={handleClick} className={baseClasses}>{content}</button>;
    $[25] = baseClasses;
    $[26] = content;
    $[27] = handleClick;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  return t9;
};
function _temp(_, i) {
  return <div key={i} className="flex items-center gap-3 p-2"><div className="w-5 h-5 bg-gray-200 rounded animate-pulse" /><div className="flex-1 h-5 bg-gray-200 rounded animate-pulse" /></div>;
}